import styled from '@emotion/styled';
import React, { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { config } from 'src/config/config';
import { toastNotificationsService } from 'src/config/rootService';
import img from 'src/public/img/link-expired.svg';
import logoImg from 'src/public/img/mh-logo-text.svg';
import { httpClient } from 'src/config/http';
import { color, font } from 'src/shared/styles/variables';

interface Props {
    token: string;
    timeBeforeNextToken: number;
}

const ContentWrapper = styled.div({
    padding: '56px',
    display: 'flex',
    textAlign: 'center',
    flexDirection: 'column',
    alignItems: 'center',
});

const HeadingText = styled.h2(
    {
        color: color.ebonyClay,
        maxWidth: '320px',
    },
    font.heavy(font.size.header),
);

const DescriptionText = styled.p(
    {
        maxWidth: '460px',
        paddingBottom: '52px',
        color: color.blueBayoux,
    },
    font.medium(font.size.medium),
);

const Link = styled('a')(
    {
        cursor: 'pointer',
        color: color.royalBlue,
        '&:hover': {
            textDecoration: 'underline',
            color: color.royalBlue,
        },
    },
    font.medium(font.size.medium),
);

const StyledParagraph = styled('p')(
    {
        color: color.blueBayoux,
    },
    font.medium(font.size.medium),
);

const BrandImage = styled.img({
    marginLeft: 32,
    marginTop: 20,
    width: '15em',
    cursor: 'pointer',
    marginRight: 12,
});

export const RegistrationLinkNotValidPage: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation<Props>();
    const helpCenterUrl = t('user:help_center.complete_url');
    const [isEmailResent, setIsEmailResent] = useState(false);
    const [isEmailResendVisible, setIsEmailResendVisible] = useState(true);

    const token = location?.state?.token;
    const timeBeforeNextToken = location?.state?.timeBeforeNextToken;

    const hitTokenApiEndpoint = async () => {
        const url = `accounts/check-signuptoken/${token}`;

        await httpClient
            .GET_UNAUTHENTICATED<string>(url)
            .catch(err => {
                if (err.body?.detail !== 'Token expired') {
                    toastNotificationsService.renderErrorToast(config.messages.genericError);
                }
            })
            .finally(() => {
                setIsEmailResent(true);
            });
    };

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (timeBeforeNextToken && timeBeforeNextToken > 0) {
            setIsEmailResendVisible(false);
            timer = setTimeout(() => {
                setIsEmailResendVisible(true);
            }, timeBeforeNextToken);
        }
        return () => clearTimeout(timer);
    }, [timeBeforeNextToken]);

    return (
        <>
            <BrandImage
                src={logoImg}
                onClick={() => {
                    window.location.href = config.modernHealthURL;
                }}
            />
            <ContentWrapper>
                <img src={img} alt="" />
                <HeadingText>{t('user:registration.link_not_valid.title')}</HeadingText>
                <DescriptionText>
                    {t('user:registration.link_not_valid.description')}
                </DescriptionText>
                {isEmailResendVisible && (
                    <div>
                        {!isEmailResent && (
                            <Trans
                                ns="user"
                                i18nKey="registration.link_not_valid.actions"
                                components={{
                                    paragraph: <StyledParagraph />,
                                    'resend-email': <Link onClick={() => hitTokenApiEndpoint()} />,
                                }}
                            />
                        )}
                        {isEmailResent && (
                            <Trans
                                ns="user"
                                i18nKey="registration.link_not_valid.actions.help"
                                components={{
                                    paragraph: <StyledParagraph />,
                                    help: (
                                        <Link
                                            onClick={() => window.open(helpCenterUrl, '_blank')}
                                        />
                                    ),
                                }}
                            />
                        )}
                    </div>
                )}
            </ContentWrapper>
        </>
    );
};
